import React, { useCallback, useEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom/client'
import axios from 'axios'
import { v4 as uuidv4 } from 'uuid'

import { SimliClient } from './SimliClient'

// Type declarations for Web Speech API
declare global {
  interface Window {
    SpeechRecognition: any;
    webkitSpeechRecognition: any;
  }
}

const sk = import.meta.env.VITE_SIMLI_API_KEY
const e11 = import.meta.env.VITE_ELEVENLABS_API_KEY

// Get our endpoint directly from the environment now (supplied in vite.config.ts)
const completionEndpoint = import.meta.env.VITE_COMPLETION_ENDPOINT

import './styles.css'


const AGENT_ID = '23951951-5ff9-0eb6-a39f-60e67f46ed22' // this comes from the agentId output from running the Eliza framework, it likely will be in uuid format, i.e. '123e4567-e89b-12d3-a456-426614174000'
const SIMLI_FACE_ID = '743d6d0b-a5f6-4274-86d4-beae23248822'
const ELEVENLABS_VOICE_ID = 'onwK4e9ZLuTAKqWW03F9'

const simliClient = new SimliClient()

const App = () => {
  const [inputText, setInputText] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isConnecting, setIsConnecting] = useState(false)
  const [error, setError] = useState('')
  const [_, setChatgptText] = useState('')
  const [startWebRTC, setStartWebRTC] = useState(false)
  const [isListening, setIsListening] = useState(false)
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [showAlert, setShowAlert] = useState(true)
  const [showSubmitAlert, setShowSubmitAlert] = useState(false)
  const cancelTokenRef = useRef<any | null>(null)
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  // Speech recognition reference for Web Speech API
  const recognitionRef = useRef<any>(null)

  // TODO: populate these from localStorage if roomid and useruuid are set, otherwise generate a random uuid
  const [roomID, setRoomID] = useState('')
  const [userUUID, setUserUUID] = useState('')

  useEffect(() => {
    const storedRoomID = localStorage.getItem('roomID')
    const storedUserUUID = localStorage.getItem('userUUID')
    if (storedRoomID && storedUserUUID) {
      setRoomID(storedRoomID)
      setUserUUID(storedUserUUID)
    } else {
      const newRoomID = uuidv4()
      const newUserUUID = uuidv4()
      setRoomID(newRoomID)
      setUserUUID(newUserUUID)
      localStorage.setItem('roomID', newRoomID)
      localStorage.setItem('userUUID', newUserUUID)
    }
  }, [])

  const initializeSimliClient = useCallback(() => {
    if (videoRef.current && audioRef.current) {
      const SimliConfig = {
        apiKey: sk,
        faceID: SIMLI_FACE_ID,
        handleSilence: true,
        videoRef: videoRef,
        audioRef: audioRef,
      }

      simliClient.Initialize(SimliConfig)
      console.log('Simli Client initialized')
    }
  }, [])

  useEffect(() => {
    initializeSimliClient()

    const handleConnected = () => {
      console.log('SimliClient is now connected!')
    }

    const handleDisconnected = () => {
      console.log('SimliClient has disconnected!')
    }

    const handleFailed = () => {
      console.log('SimliClient has failed to connect!')
      setError('Failed to connect to Simli. Please try again.')
    }

    const handleStarted = () => {
      console.log('SimliClient has started!')
      setIsLoading(false)
      setIsConnecting(false)
    }

    simliClient.on('connected', handleConnected)
    simliClient.on('disconnected', handleDisconnected)
    simliClient.on('failed', handleFailed)
    simliClient.on('started', handleStarted)

    return () => {
      simliClient.off('connected', handleConnected)
      simliClient.off('disconnected', handleDisconnected)
      simliClient.off('failed', handleFailed)
      simliClient.off('started', handleStarted)
      simliClient.close()
    }
  }, [initializeSimliClient])

  const handleStart = useCallback(() => {
    simliClient.start()
    setStartWebRTC(true)
    setIsLoading(true)
    setIsConnecting(true)

    setTimeout(() => {
      const audioData = new Uint8Array(6000).fill(0)
      simliClient.sendAudioData(audioData)
    }, 4000)
  }, [])

  const processInput = useCallback(async (text: any) => {
    setIsLoading(true)
    setError('')

    if (cancelTokenRef.current) {
      cancelTokenRef.current.cancel('Operation canceled by the user.')
    }

    cancelTokenRef.current = axios.CancelToken.source()

    try {
      console.log('sending input to chatgpt')
      // Always use the completionEndpoint (which includes the CORS proxy in production)
      const apiPath = `${completionEndpoint}/${AGENT_ID}/message`
      console.log('Using API endpoint:', apiPath)
      
      const chatGPTResponse = await axios.post(
        apiPath,
        {
          text,
          roomId: roomID,
          userId: userUUID,
          userName: 'User',
        },
        {
          cancelToken: cancelTokenRef.current.token,
        }
      )

      console.log('chatGPTResponse', chatGPTResponse)

      const chatGPTText = chatGPTResponse.data[0].text
      if (!chatGPTText || chatGPTText.length === 0) {
        setError('No response from chatGPT. Please try again.')
        return
      }
      setChatgptText(chatGPTText)

      const elevenlabsResponse = await axios.post(
        `https://api.elevenlabs.io/v1/text-to-speech/${ELEVENLABS_VOICE_ID}?output_format=pcm_16000`,
        {
          text: chatGPTText,
          model_id: 'eleven_turbo_v2_5',
        },
        {
          headers: {
            'xi-api-key': e11,
            'Content-Type': 'application/json',
          },
          responseType: 'arraybuffer',
          cancelToken: cancelTokenRef.current.token,
        }
      )

      const pcm16Data = new Uint8Array(elevenlabsResponse.data)
      const chunkSize = 6000
      for (let i = 0; i < pcm16Data.length; i += chunkSize) {
        const chunk = pcm16Data.slice(i, i + chunkSize)
        simliClient.sendAudioData(chunk)
      }
    } catch (err) {
      if (axios.isCancel(err)) {
        console.log('Request canceled:', err.message)
      } else {
        setError('An error occurred. Please try again.')
        console.error(err)
      }
    } finally {
      setIsLoading(false)
      cancelTokenRef.current = null
    }
  }, [])

  // Client-side speech recognition implementation
  const startListening = useCallback(() => {
    try {
      // Check if browser supports speech recognition
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition
      if (!SpeechRecognition) {
        throw new Error('Speech recognition not supported in this browser')
      }

      // Create and configure speech recognition
      recognitionRef.current = new SpeechRecognition()
      if (recognitionRef.current) {
        recognitionRef.current.continuous = true
        recognitionRef.current.interimResults = false
        recognitionRef.current.lang = 'en-US'
        recognitionRef.current.maxAlternatives = 1

        // Handle recognition results
        recognitionRef.current.onresult = (event: any) => {
          const transcript = event.results[event.results.length - 1][0].transcript
          console.log('Recognized text:', transcript)
          if (transcript.trim()) {
            // Show alert before processing transcript
            setShowSubmitAlert(true)
            // Process the transcript directly
            processInput(transcript)
          }
        }

        // Handle errors
        recognitionRef.current.onerror = (event: any) => {
          console.error('Speech recognition error:', event.error)
          setError(`Speech recognition error: ${event.error}`)
        }

        // Handle recognition ending
        recognitionRef.current.onend = () => {
          // If we're still in listening mode but recognition ended, restart it
          if (isListening && recognitionRef.current) {
            recognitionRef.current.start()
          }
        }
      }

      // Start recognition
      if (recognitionRef.current) {
        recognitionRef.current.start()
        setIsListening(true)
        setError('') // Clear any previous error
      }
    } catch (err) {
      console.error('Error starting speech recognition:', err)
      setError('Error starting speech recognition. ' + (err instanceof Error ? err.message : 'Please try again.'))
      setIsListening(false)
    }
  }, [isListening, processInput, setError])

  const stopListening = useCallback(() => {
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop()
      } catch (error) {
        console.error('Error stopping speech recognition:', error)
      }
    }
    console.log('Stopping speech recognition')
    setIsListening(false)
  }, [setIsListening])
  
  // Type declaration is already at the top of the file
  
  // Toggle listening function using the speech recognition functions defined above
  const toggleListening = useCallback(() => {
    if (isListening) {
      console.log('Stopping speech recognition')
      stopListening()
    } else {
      console.log('Starting speech recognition')
      startListening()
    }
  }, [isListening, stopListening, startListening])
  
  // Initial alert auto-dismiss effect
  useEffect(() => {
    if (showAlert) {
      const timer = setTimeout(() => {
        setShowAlert(false)
      }, 8000) // 8 seconds
      return () => clearTimeout(timer)
    }
  }, [showAlert])

  // Submit alert auto-dismiss effect
  useEffect(() => {
    if (showSubmitAlert) {
      const timer = setTimeout(() => {
        setShowSubmitAlert(false)
      }, 11000) // 11 seconds
      return () => clearTimeout(timer)
    }
  }, [showSubmitAlert])

  const handleSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      const input = inputText.trim()
      setInputText('')
      setShowSubmitAlert(true)
      await processInput(input)
    },
    [inputText, processInput]
  )

  return (
    <div className="relative overflow-x-hidden">
      {/* Alert container with higher z-index */}
      <div className="fixed inset-0 pointer-events-none z-[100] overflow-hidden" aria-hidden="true">
        {/* Alert Popup */}
        {showAlert && (
          <div className="pointer-events-auto absolute top-4 right-0 mr-2 sm:mr-4">
            <div className="w-64 sm:w-72 bg-gray-900 border border-gray-700 rounded-lg shadow-lg p-3 animate-fade-in-down">
              <div className="flex flex-col items-center text-center text-white">
                <h3 className="text-lg sm:text-xl font-semibold mb-2">Chat directly with Grantolf the Grants Wizard</h3>
                <p className="text-xs sm:text-sm mb-2">Please note this tool is in beta and may be buggy so please refresh if it doesn't work the first time.</p>
                <p className="text-base sm:text-lg font-bold italic mt-1">Go forth and apply you fools!</p>
              </div>
            </div>
          </div>
        )}
        
        {/* Submit Confirmation Alert */}
        {showSubmitAlert && (
          <div className="pointer-events-auto absolute top-4 right-0 mr-2 sm:mr-4">
            <div className="w-64 sm:w-72 bg-gray-800 border border-blue-600 rounded-lg shadow-lg p-3 animate-fade-in-down">
              <div className="flex flex-col items-center text-center text-white">
                <p className="text-base sm:text-lg font-bold italic">Your query has been sent forth to the wise Grantolf the Grants Wizard. 🧙🏼‍♂️ He'll reply precisely when he means to.</p>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Logo in the upper left */}
      <div className="fixed top-4 left-4 z-50">
        <a 
          href="https://grantolf.com" 
          target="_blank" 
          rel="noopener noreferrer"
        >
          <img
            src="/grantolf.png" 
            alt="Grantolf Logo"
            className="h-20 w-auto transition-transform duration-200 hover:scale-105"
            onError={(e) => {
              // Fallback if image isn't available yet
              const target = e.target as HTMLImageElement;
              target.style.display = 'none';
            }}
          />
        </a>
      </div>
      
      {/* Hamburger Menu Button */}
      <button 
        className="fixed top-4 right-4 z-50 p-2 rounded-md bg-gray-800 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-white"
        onClick={() => setIsMenuOpen(!isMenuOpen)}
        aria-label="Toggle menu"
      >
        <svg 
          className="w-6 h-6 text-white" 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24" 
          xmlns="http://www.w3.org/2000/svg"
        >
          {isMenuOpen ? (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          ) : (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          )}
        </svg>
      </button>

      {/* Mobile Menu */}
      <div 
        className={`fixed inset-0 z-40 transform transition-transform duration-300 ease-in-out ${isMenuOpen ? 'translate-x-0' : 'translate-x-full'}`}
      >
        <div className="fixed inset-0 bg-black opacity-50" onClick={() => setIsMenuOpen(false)}></div>
        <div className="fixed right-0 h-full w-64 bg-gray-900 p-5 text-white shadow-lg transform transition-transform duration-300 ease-in-out">
          <div className="flex flex-col space-y-6 pt-10">
            <a 
              href="https://grantolf.com/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-white hover:text-gray-300 transition-colors duration-200"
            >
              Back to the Grantolf dapp
            </a>
            <a 
              href="https://app.uniswap.org/swap?chain=polygon&inputCurrency=0x7ceb23fd6bc0add59e62ac25578270cff1b9f619&outputCurrency=0xdb7a2607b71134d0b09c27ca2d77b495e4dbeedb&value=.01&field=input" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-white hover:text-gray-300 transition-colors duration-200"
            >
              Buy $GRANTS
            </a>
            <a 
              href="https://twitter.com/grantolfthegrey" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-white hover:text-gray-300 transition-colors duration-200"
            >
              Follow Grantolf on X
            </a>
          </div>
        </div>
      </div>

      <div className='flex h-screen w-full flex-col items-center justify-center font-mono text-white'>
        <div className='relative size-full'>
          <video
            ref={videoRef}
            id='simli_video'
            autoPlay
            playsInline
            className='size-full object-contain'
          ></video>
          <audio ref={audioRef} id='simli_audio' autoPlay></audio>
        </div>
        {startWebRTC && !isConnecting ? (
          <>
            {/* {chatgptText && <p className='text-center'>{chatgptText}</p>} */}
            <form
              onSubmit={handleSubmit}
              className='fixed bottom-4 left-1/2 transform -translate-x-1/2 w-full max-w-xl space-y-4 px-4'
            >
              <div className='flex w-full items-center space-x-3'>
                <textarea
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey && inputText.trim()) {
                      e.preventDefault();
                      handleSubmit(e as unknown as React.FormEvent<HTMLFormElement>);
                    }
                  }}
                  placeholder='Type or record your query to the wise Grantolf the Grants Wizard'
                  rows={2}
                  className='grow rounded border border-white bg-black px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-white min-w-[240px] text-base w-full resize-none'
                />
                <button
                  type='submit'
                  disabled={isLoading || !inputText.trim()}
                  className='rounded bg-white px-3 py-1 text-3xl text-black transition-colors hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-black disabled:opacity-50'
                >
                  {isLoading ? '➡️' : '➡️'}
                </button>
                <button
                  type='button'
                  onClick={toggleListening}
                  className='rounded bg-blue-500 px-3 py-1 text-2xl text-white transition-colors hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-black'
                >
                  {isListening ? '🔴' : '🎤'}
                </button>
              </div>
            </form>
          </>
        ) : (
          <>
            {isConnecting && (
              <p className='fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2'>
                Connecting...
              </p>
            )}
            {!isConnecting && (
              <button
                disabled={isConnecting}
                onClick={handleStart}
                className='fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded bg-white px-4 py-2 text-black transition-colors hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-black'
              >
                🧙🏼‍♂️ Summon the Grants Wizard
              </button>
            )}
          </>
        )}
        {error && <p className='fixed bottom-20 mt-4 text-center text-red-500'>{error}</p>}
      </div>
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundImage: 'url(./bg.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          zIndex: -1000,
        }}
      />
    </div>
  )
}

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
)
